import { motion } from 'motion/react';
import { ArrowLeft, Search, Check } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useState } from 'react';
import { FlagImage } from './utils/countryFlags';
import { toast } from 'sonner';
import { ParallaxBackground } from './utils/parallax';

interface AllFlagsScreenProps {
  currentCountry: string;
  onBack: () => void;
  onSave: (selection: { name: string; code: string; flag: string; type: 'country' | 'region' }) => void;
}

// All African Countries
const allCountries = [
  // North Africa
  { name: 'Algeria', code: 'dz', region: 'North Africa' },
  { name: 'Egypt', code: 'eg', region: 'North Africa' },
  { name: 'Libya', code: 'ly', region: 'North Africa' },
  { name: 'Morocco', code: 'ma', region: 'North Africa' },
  { name: 'Sudan', code: 'sd', region: 'North Africa' },
  { name: 'Tunisia', code: 'tn', region: 'North Africa' },

  // West Africa
  { name: 'Benin', code: 'bj', region: 'West Africa' },
  { name: 'Burkina Faso', code: 'bf', region: 'West Africa' },
  { name: 'Cape Verde', code: 'cv', region: 'West Africa' },
  { name: 'Ivory Coast', code: 'ci', region: 'West Africa' },
  { name: 'Gambia', code: 'gm', region: 'West Africa' },
  { name: 'Ghana', code: 'gh', region: 'West Africa' },
  { name: 'Guinea', code: 'gn', region: 'West Africa' },
  { name: 'Guinea-Bissau', code: 'gw', region: 'West Africa' },
  { name: 'Liberia', code: 'lr', region: 'West Africa' },
  { name: 'Mali', code: 'ml', region: 'West Africa' },
  { name: 'Mauritania', code: 'mr', region: 'West Africa' },
  { name: 'Niger', code: 'ne', region: 'West Africa' },
  { name: 'Nigeria', code: 'ng', region: 'West Africa' },
  { name: 'Senegal', code: 'sn', region: 'West Africa' },
  { name: 'Sierra Leone', code: 'sl', region: 'West Africa' },
  { name: 'Togo', code: 'tg', region: 'West Africa' },

  // East Africa
  { name: 'Burundi', code: 'bi', region: 'East Africa' },
  { name: 'Comoros', code: 'km', region: 'East Africa' },
  { name: 'Djibouti', code: 'dj', region: 'East Africa' },
  { name: 'Eritrea', code: 'er', region: 'East Africa' },
  { name: 'Ethiopia', code: 'et', region: 'East Africa' },
  { name: 'Kenya', code: 'ke', region: 'East Africa' },
  { name: 'Madagascar', code: 'mg', region: 'East Africa' },
  { name: 'Malawi', code: 'mw', region: 'East Africa' },
  { name: 'Mauritius', code: 'mu', region: 'East Africa' },
  { name: 'Mozambique', code: 'mz', region: 'East Africa' },
  { name: 'Rwanda', code: 'rw', region: 'East Africa' },
  { name: 'Seychelles', code: 'sc', region: 'East Africa' },
  { name: 'Somalia', code: 'so', region: 'East Africa' },
  { name: 'South Sudan', code: 'ss', region: 'East Africa' },
  { name: 'Tanzania', code: 'tz', region: 'East Africa' },
  { name: 'Uganda', code: 'ug', region: 'East Africa' },
  { name: 'Zambia', code: 'zm', region: 'East Africa' },
  { name: 'Zimbabwe', code: 'zw', region: 'East Africa' },

  // Central Africa
  { name: 'Angola', code: 'ao', region: 'Central Africa' },
  { name: 'Cameroon', code: 'cm', region: 'Central Africa' },
  { name: 'CAR', code: 'cf', region: 'Central Africa' },
  { name: 'Chad', code: 'td', region: 'Central Africa' },
  { name: 'Congo', code: 'cg', region: 'Central Africa' },
  { name: 'DR Congo', code: 'cd', region: 'Central Africa' },
  { name: 'Equatorial Guinea', code: 'gq', region: 'Central Africa' },
  { name: 'Gabon', code: 'ga', region: 'Central Africa' },
  { name: 'São Tomé', code: 'st', region: 'Central Africa' },

  // Southern Africa
  { name: 'Botswana', code: 'bw', region: 'Southern Africa' },
  { name: 'eSwatini', code: 'sz', region: 'Southern Africa' },
  { name: 'Lesotho', code: 'ls', region: 'Southern Africa' },
  { name: 'Namibia', code: 'na', region: 'Southern Africa' },
  { name: 'South Africa', code: 'za', region: 'Southern Africa' },

  // Caribbean Nations
  { name: 'Antigua and Barbuda', code: 'ag', region: 'Caribbean' },
  { name: 'Bahamas', code: 'bs', region: 'Caribbean' },
  { name: 'Barbados', code: 'bb', region: 'Caribbean' },
  { name: 'Dominica', code: 'dm', region: 'Caribbean' },
  { name: 'Grenada', code: 'gd', region: 'Caribbean' },
  { name: 'Haiti', code: 'ht', region: 'Caribbean' },
  { name: 'Jamaica', code: 'jm', region: 'Caribbean' },
  { name: 'Saint Lucia', code: 'lc', region: 'Caribbean' },
  { name: 'Saint Vincent and the Grenadines', code: 'vc', region: 'Caribbean' },
  { name: 'Trinidad and Tobago', code: 'tt', region: 'Caribbean' },
  { name: 'Belize', code: 'bz', region: 'Caribbean' },
  { name: 'Guyana', code: 'gy', region: 'Caribbean' },
  { name: 'Saint Kitts and Nevis', code: 'kn', region: 'Caribbean' },
  { name: 'Suriname', code: 'sr', region: 'Caribbean' },

  // Diaspora Countries / Regional
  { name: 'Black American/Soulaan', code: 'soulaan', region: 'North American' },
  { name: 'United States', code: 'us', region: 'North American' },
  { name: 'Canada', code: 'ca', region: 'North American' },
  { name: 'Republic of New Afrika', code: 'rna', region: 'North American' },
  { name: 'Gullah Geechie', code: 'gg', region: 'North American' },
  { name: 'Mexico', code: 'mx', region: 'North American' },
  { name: 'Costa Rica', code: 'cr', region: 'North American' },
  { name: 'Cuba', code: 'cu', region: 'North American' },
  { name: 'Panama', code: 'pa', region: 'North American' },
  { name: 'Dominican Republic', code: 'do', region: 'Caribbean' },

  // South American
  { name: 'Brazil', code: 'br', region: 'South American' },
  { name: 'Argentina', code: 'ar', region: 'South American' },
  { name: 'Chile', code: 'cl', region: 'South American' },
  { name: 'Colombia', code: 'co', region: 'South American' },
  { name: 'Peru', code: 'pe', region: 'South American' },
  { name: 'Venezuela', code: 've', region: 'South American' },
  { name: 'Uruguay', code: 'uy', region: 'South American' },
  { name: 'Paraguay', code: 'py', region: 'South American' },
  { name: 'Bolivia', code: 'bo', region: 'South American' },
  { name: 'Ecuador', code: 'ec', region: 'South American' },

  // European
  { name: 'United Kingdom', code: 'gb', region: 'European' },
  { name: 'France', code: 'fr', region: 'European' },
  { name: 'Germany', code: 'de', region: 'European' },
  { name: 'Netherlands', code: 'nl', region: 'European' },
  { name: 'Belgium', code: 'be', region: 'European' },
  { name: 'Portugal', code: 'pt', region: 'European' },
  { name: 'Spain', code: 'es', region: 'European' },
  { name: 'Italy', code: 'it', region: 'European' },
  { name: 'Sweden', code: 'se', region: 'European' },
  { name: 'Norway', code: 'no', region: 'European' },
  { name: 'Denmark', code: 'dk', region: 'European' },
  { name: 'Switzerland', code: 'ch', region: 'European' },
  { name: 'Austria', code: 'at', region: 'European' },
  { name: 'Ireland', code: 'ie', region: 'European' },
  { name: 'Poland', code: 'pl', region: 'European' },
  { name: 'Romania', code: 'ro', region: 'European' },
  { name: 'Ukraine', code: 'ua', region: 'European' },
  { name: 'Greece', code: 'gr', region: 'European' },
  { name: 'Finland', code: 'fi', region: 'European' },
  { name: 'Iceland', code: 'is', region: 'European' },
  { name: 'Albania', code: 'al', region: 'European' },

  // Asian / Middle East
  { name: 'China', code: 'cn', region: 'Asian' },
  { name: 'India', code: 'in', region: 'Asian' },
  { name: 'Japan', code: 'jp', region: 'Asian' },
  { name: 'South Korea', code: 'kr', region: 'Asian' },
  { name: 'Singapore', code: 'sg', region: 'Asian' },
  { name: 'Malaysia', code: 'my', region: 'Asian' },
  { name: 'Thailand', code: 'th', region: 'Asian' },
  { name: 'Indonesia', code: 'id', region: 'Asian' },
  { name: 'Philippines', code: 'ph', region: 'Asian' },
  { name: 'Vietnam', code: 'vn', region: 'Asian' },
  { name: 'Pakistan', code: 'pk', region: 'Asian' },
  { name: 'Bangladesh', code: 'bd', region: 'Asian' },
  { name: 'Afghanistan', code: 'af', region: 'Asian' },
  { name: 'Turkey', code: 'tr', region: 'Asian' },
  { name: 'Israel', code: 'il', region: 'Asian' },
  { name: 'Saudi Arabia', code: 'sa', region: 'Asian' },
  { name: 'UAE', code: 'ae', region: 'Asian' },
  { name: 'Qatar', code: 'qa', region: 'Asian' },
  { name: 'Oman', code: 'om', region: 'Asian' },
  { name: 'Yemen', code: 'ye', region: 'Asian' },
  { name: 'Palestine', code: 'ps', region: 'Asian' },

  // Oceania
  { name: 'Australia', code: 'au', region: 'Oceania' },
  { name: 'New Zealand', code: 'nz', region: 'Oceania' },
  { name: 'Fiji', code: 'fj', region: 'Oceania' },
  { name: 'Papua New Guinea', code: 'pg', region: 'Oceania' },
  { name: 'Samoa', code: 'ws', region: 'Oceania' },
  { name: 'Tonga', code: 'to', region: 'Oceania' },
  { name: 'Vanuatu', code: 'vu', region: 'Oceania' },
];

export function AllFlagsScreen({ currentCountry, onBack, onSave }: AllFlagsScreenProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<{ name: string; code: string; flag: string; type: 'country' | 'region' } | null>(null);

  // Filter countries based on search
  const filteredCountries = allCountries.filter((country) =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.region.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group countries by region
  const groupedCountries = filteredCountries.reduce((acc: Record<string, typeof allCountries>, country) => {
    if (!acc[country.region]) {
      acc[country.region] = [];
    }
    acc[country.region].push(country);
    return acc;
  }, {} as Record<string, typeof allCountries>);

  const handleSelectCountry = (country: typeof allCountries[0]) => {
    setSelectedItem({
      name: country.name,
      code: country.code,
      flag: country.code,
      type: 'country'
    });
  };

  const handleSave = () => {
    if (!selectedItem) {
      toast.error('Please select a flag');
      return;
    }

    onSave(selectedItem as { name: string; code: string; flag: string; type: 'country' | 'region' });
  };

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/30 backdrop-blur-lg"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="text-[#2C3E50] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
              >
                <ArrowLeft className="w-6 h-6" />
              </motion.button>
              <h1 className="text-2xl header-text-shadow">
                All Flags
              </h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={!selectedItem}
              className="bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white disabled:opacity-50"
            >
              <Check className="w-4 h-4 mr-2" />
              Save Selection
            </Button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search countries or regions..."
              className="w-full pl-12 h-12 bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] rounded-xl"
            />
          </div>

        </div>

        {/* Content */}
        <div className="flex-1 bg-white/30 dark:bg-black/10 min-h-0 overflow-y-auto w-full touch-pan-y">
          <div className="p-4 space-y-8 pb-20">
            {Object.entries(groupedCountries).map(([region, countries]) => (
              <div key={region} className="space-y-3">
                <h2 className="text-lg font-bold text-black dark:text-white px-2 border-l-4 border-[#F39C12] dark:border-[#4478ea] ml-1">
                  {region}
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {countries.map((country) => (
                    <motion.button
                      key={country.code}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectCountry(country)}
                      className={`w-full p-4 rounded-xl border-2 transition-all ${selectedItem?.code === country.code
                        ? 'border-[#F39C12] bg-[#F39C12]/10 dark:border-[#4478ea] dark:bg-[#4478ea]/20'
                        : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#1a1a1a] hover:border-[#F39C12]/50 dark:hover:border-[#4478ea]/50'
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-12 rounded-lg overflow-hidden border border-black/10 dark:border-white/10 shrink-0">
                          <FlagImage countryName={country.name} size="w80" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-black dark:text-white font-medium">{country.name}</p>
                          <p className="text-xs text-[#666666] dark:text-[#E5E5E5] uppercase tracking-wider">{country.region}</p>
                        </div>
                        {selectedItem?.code === country.code && (
                          <Check className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))}

            {filteredCountries.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#666666] dark:text-[#E5E5E5]">No countries found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
